/*  Shared  */
import { Dropdown, DropdownMenu } from 'application/Shared/Dropdown';

/*  React  */
import React from 'react';

/* Internal Components */
import { useManagedTableV2Context } from '../ManagedTableV2ContextProvider';
import { CustomTableContext, ShowFlyoutMenuActionEnum, View } from '../types';

export const ManagedTableViews = <T,>({
    'data-testid': dataTestId = 'managedTableViewsBtn'
}: {
    /**
     * The data test id for this managedTableViews component.
     */
    'data-testid': string;
}) => {
    const {
        currentTableView,
        customTableViews,
        defaultView,
        setCurrentTableView,
        setShowFlyoutMenu,
        tableName,
        tableViews
    } = useManagedTableV2Context<T>() as CustomTableContext<T>;

    const disableEditButton = currentTableView?.id?.includes('_presetView');

    const menu: DropdownMenu = [
        {
            labelKey: 'TableViews.changeView',
            type: 'header'
        },
        {
            dataTrackid: `tableViews_${tableName}_presetViews`,
            labelKey: 'TableViews.presetViews',
            submenu: tableViews?.map((view: View) => ({
                action: () => {
                    setShowFlyoutMenu(ShowFlyoutMenuActionEnum.hide);
                    setCurrentTableView(view);
                },
                dataTrackid: `tableViews_${tableName}_presetViews_${view.displayName}`,
                labelKey: view.displayName,
                prefix: currentTableView?.id === view.id ? 'Check' : undefined,
                type: 'leaf'
            })),
            type: 'submenu'
        },
        {
            dataTrackid: `tableViews_${tableName}_savedViews`,
            divider: true,
            labelKey: 'TableViews.savedViews',
            submenu: customTableViews?.map((view) => ({
                action: () => {
                    setShowFlyoutMenu(ShowFlyoutMenuActionEnum.hide);
                    setCurrentTableView(view);
                },
                dataTrackid: `tableViews_${tableName}_savedViews_${view.displayName}`,
                labelKey: view.displayName,
                prefix: currentTableView?.id === view.id ? 'Check' : undefined,
                type: 'leaf'
            })),
            type: 'submenu'
        },
        {
            labelKey: 'TableViews.manageViews',
            type: 'header'
        },
        {
            action: () => {
                setShowFlyoutMenu(ShowFlyoutMenuActionEnum.edit);
            },
            dataTrackid: `tableViews_${tableName}_edit_${currentTableView?.displayName}`,
            disabled: disableEditButton,
            labelKey: 'TableViews.edit',
            type: 'leaf'
        },
        {
            action: () => {
                setShowFlyoutMenu(ShowFlyoutMenuActionEnum.delete);
            },
            dataTrackid: `tableViews_${tableName}_delete`,
            disabled: !customTableViews?.length,
            labelKey: 'TableViews.delete',
            type: 'leaf'
        },
        {
            action: () => {
                setShowFlyoutMenu(ShowFlyoutMenuActionEnum.save);
            },
            dataTrackid: `tableViews_${tableName}_saveAsNewView`,
            labelKey: 'TableViews.saveAsNewView',
            type: 'leaf'
        },
        {
            action: () => {
                setShowFlyoutMenu(ShowFlyoutMenuActionEnum.hide);
                if (defaultView) {
                    setCurrentTableView(defaultView);
                }
            },
            dataTrackid: `tableViews_${tableName}_resetToDefault`,
            labelKey: 'TableViews.resetToDefault',
            type: 'leaf'
        }
    ];

    return (
        <Dropdown
            data-trackid={`tableViews_${tableName}`}
            data-testid={`${dataTestId}_dropdown`}
            labelKey="TableViews.views"
            menu={menu}
        />
    );
};
